import { useState } from "react";
import adobe from "../brand/companies/adobe.png";
import anthropic from "../brand/companies/anthropic.png";
import bosch from "../brand/companies/bosch.png";
import databricks from "../brand/companies/databricks.png";
import figma from "../brand/companies/figma.png";
import linear from "../brand/companies/linear.png";
import meta from "../brand/companies/meta.png";
import notion from "../brand/companies/notion.png";
import plaid from "../brand/companies/plaid.png";
import ramp from "../brand/companies/ramp.png";
import salesforce from "../brand/companies/salesforce.png";
import stripe from "../brand/companies/stripe.png";
import vercel from "../brand/companies/vercel.png";
import { cn } from "./cn";

function initials(name: string) {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

const sizes = {
  sm: { tile: "size-8 rounded-[4px] text-[12px]", pad: "p-1", px: 32 },
  md: { tile: "size-11 rounded-[6px] text-[15px]", pad: "p-1.5", px: 44 },
  lg: { tile: "size-14 rounded-[8px] text-lg", pad: "p-2", px: 56 },
};

/** Logo URLs that 404ed or failed to decode this session, so a list of rows never asks twice. */
const failed = new Set<string>();
/** Logo URLs that loaded this session, so a remount paints the image without fading in again. */
const loaded = new Set<string>();

/** Only the host and board slug of a job URL matter to the resolver, so every job at a company shares one cached URL. */
function hint(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    const slug = u.pathname.split("/").filter(Boolean)[0] ?? "";
    const board = u.searchParams.get("for");
    return `${u.origin}/${slug}${board ? `?for=${encodeURIComponent(board)}` : ""}`;
  } catch {
    return null;
  }
}

/** Logos bundled with the app, by domain, so the public landing draws its companies without asking any provider. */
export const BUNDLED_LOGOS: Record<string, string> = {
  "adobe.com": adobe,
  "anthropic.com": anthropic,
  "bosch.com": bosch,
  "databricks.com": databricks,
  "figma.com": figma,
  "linear.app": linear,
  "meta.com": meta,
  "notion.so": notion,
  "notion.com": notion,
  "plaid.com": plaid,
  "ramp.com": ramp,
  "salesforce.com": salesforce,
  "stripe.com": stripe,
  "vercel.com": vercel,
};

/**
 * Where a company's logo comes from: a bundled file when `domain` is one we ship, else the `/api/logo` URL. Null for
 * names that are not companies, such as an email address.
 */
export function logoSrc(name: string, url?: string | null, domain?: string | null): string | null {
  const company = name.trim();
  if (!company || company === "?" || company.includes("@")) return null;
  const d = domain?.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/^www\./, "");
  if (d && Object.hasOwn(BUNDLED_LOGOS, d)) return BUNDLED_LOGOS[d]!;
  const q = new URLSearchParams({ company });
  if (d) q.set("domain", d);
  const h = d ? null : hint(url);
  if (h) q.set("url", h);
  return `/api/logo?${q}`;
}

type Status = "loading" | "ok" | "failed";
const statusOf = (src: string | null): Status => (!src || failed.has(src) ? "failed" : loaded.has(src) ? "ok" : "loading");

/**
 * A company's logo on a white tile, or its initials on a neutral tile when no logo resolves. `domain`, the company's
 * site, or `url`, a job or apply URL, picks the right company when names collide. Pass `logo={false}` for people,
 * whose names are not brands.
 */
export function Monogram({
  name,
  size = "md",
  className,
  url,
  domain,
  logo = true,
}: {
  name: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  url?: string | null;
  domain?: string | null;
  logo?: boolean;
}) {
  const src = logo ? logoSrc(name, url, domain) : null;
  const [seen, setSeen] = useState<{ src: string | null; status: Status }>(() => ({ src, status: statusOf(src) }));
  const status = seen.src === src ? seen.status : statusOf(src);
  const s = sizes[size];
  const tile = "inline-grid shrink-0 place-items-center overflow-hidden border border-line select-none";

  if (!src || status === "failed") {
    return (
      <span aria-hidden className={cn(tile, "bg-sunk font-bold tracking-[-0.02em] text-ink", s.tile, className)}>
        {initials(name)}
      </span>
    );
  }

  return (
    <span aria-hidden className={cn(tile, "bg-surface", s.tile, className)}>
      <img
        src={src}
        alt=""
        width={s.px}
        height={s.px}
        loading="lazy"
        decoding="async"
        draggable={false}
        onLoad={() => {
          loaded.add(src);
          setSeen({ src, status: "ok" });
        }}
        onError={() => {
          failed.add(src);
          setSeen({ src, status: "failed" });
        }}
        className={cn("size-full object-contain transition-opacity duration-150", s.pad, status === "ok" ? "opacity-100" : "opacity-0")}
      />
    </span>
  );
}
